import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Building2, 
  MapPin, 
  DollarSign,
  Users,
  Calendar,
  Edit,
  Save,
  X,
  Building,
  Phone,
  Mail,
  FileText,
  Clock,
  Link,
  Unlink
} from 'lucide-react';
import apiService from '../services/api';

const OfficeProfile = ({ office, onClose, onUpdate, userRole }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [editedOffice, setEditedOffice] = useState(office);
  const [assignedMember, setAssignedMember] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadRelatedData();
  }, [office.id]);

  const loadRelatedData = async () => {
    setLoading(true);
    try {
      // Load members to find assigned member
      const membersData = await apiService.getMembers();
      setMembers(membersData);
      
      // Find assigned member for this office
      const assigned = membersData.find(member => member.office_id === office.id);
      setAssignedMember(assigned);
    } catch (error) {
      console.error('Error loading related data:', error);
      setError('Failed to load related information');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await apiService.updateOffice(office.id, editedOffice);
      setIsEditing(false);
      if (onUpdate) onUpdate(editedOffice);
    } catch (error) {
      console.error('Error updating office:', error);
      setError('Failed to update office');
    }
  };

  const handleCancel = () => {
    setEditedOffice(office);
    setIsEditing(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Vacant':
        return 'bg-gray-100 text-gray-800';
      case 'Leased':
        return 'bg-green-100 text-green-800';
      case 'Maintenance':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getLeaseStatus = () => {
    if (!office.lease_info?.start_date) return 'No Lease';
    
    const startDate = new Date(office.lease_info.start_date);
    const termMonths = office.lease_info.term_months || 12;
    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + termMonths);
    
    const now = new Date();
    const daysUntilEnd = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilEnd < 0) return 'Expired';
    if (daysUntilEnd < 30) return 'Expiring Soon';
    return 'Active';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const handleUnassignMember = async () => {
    try {
      if (assignedMember) {
        await apiService.assignMemberToOffice(assignedMember.id, null);
        setAssignedMember(null);
        // Reload data to refresh
        loadRelatedData();
      }
    } catch (error) {
      console.error('Error unassigning member:', error);
      setError('Failed to unassign member');
    }
  };

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-start p-6 border-b border-gray-200">
        <div className="flex items-start space-x-4">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
            <Building2 className="w-8 h-8 text-blue-600" />
          </div>
          
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h2 className="text-2xl font-bold text-gray-900">
                {office.type} {office.office_number}
              </h2>
              <Badge className={getStatusColor(office.status)}>
                {office.status}
              </Badge>
            </div>
            
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center">
                <MapPin className="w-4 h-4 mr-1" />
                <span>{office.location_name}</span>
              </div>
              <div className="flex items-center">
                <Users className="w-4 h-4 mr-1" />
                <span>{office.desks} desks • Floor {office.floor}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {isEditing ? (
            <>
              <Button onClick={handleSave} size="sm">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button onClick={handleCancel} variant="outline" size="sm">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </>
          ) : (
            userRole === 'admin' && (
              <Button onClick={() => setIsEditing(true)} variant="outline" size="sm">
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )
          )}
          
          <Button onClick={onClose} variant="outline" size="sm">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {error && (
        <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="pricing">
              <DollarSign className="w-4 h-4 mr-2" />
              Pricing
            </TabsTrigger>
            <TabsTrigger value="assignment">
              <Building className="w-4 h-4 mr-2" />
              Assignment
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Office Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isEditing ? (
                    <>
                      <div>
                        <Label htmlFor="type">Office Type</Label>
                        <Input
                          id="type"
                          value={editedOffice.type || ''}
                          onChange={(e) => setEditedOffice({...editedOffice, type: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="office_number">Office Number</Label>
                        <Input
                          id="office_number"
                          value={editedOffice.office_number || ''}
                          onChange={(e) => setEditedOffice({...editedOffice, office_number: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="desks">Number of Desks</Label>
                        <Input
                          id="desks"
                          type="number"
                          value={editedOffice.desks || ''}
                          onChange={(e) => setEditedOffice({...editedOffice, desks: parseInt(e.target.value)})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="floor">Floor</Label>
                        <Input
                          id="floor"
                          value={editedOffice.floor || ''}
                          onChange={(e) => setEditedOffice({...editedOffice, floor: e.target.value})}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Type:</span>
                        <span className="font-medium">{office.type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Office Number:</span>
                        <span className="font-medium">{office.office_number}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Desks:</span>
                        <span className="font-medium">{office.desks}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Floor:</span>
                        <span className="font-medium">{office.floor}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Location:</span>
                        <span className="font-medium">{office.location_name}</span>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Lease Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    Lease Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <Badge className={office.lease_info?.start_date ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {getLeaseStatus()}
                    </Badge>
                  </div>
                  {office.lease_info?.start_date && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Start Date:</span>
                        <span className="font-medium">{formatDate(office.lease_info.start_date)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Term:</span>
                        <span className="font-medium">{office.lease_info.term_months} months</span>
                      </div>
                      {office.lease_info.signed_rate && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Signed Rate:</span>
                          <span className="font-medium text-green-600">
                            {formatCurrency(office.lease_info.signed_rate)}/month
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Market Rates</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="text-center">
                      <p className="text-sm text-blue-600 font-medium">Monthly</p>
                      <p className="text-xl font-bold text-blue-800">
                        {office.market_rates?.monthly ? formatCurrency(office.market_rates.monthly) : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="text-center">
                      <p className="text-sm text-amber-600 font-medium">6 Month</p>
                      <p className="text-xl font-bold text-amber-800">
                        {office.market_rates?.six_month ? formatCurrency(office.market_rates.six_month) : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="text-center">
                      <p className="text-sm text-emerald-600 font-medium">12 Month</p>
                      <p className="text-xl font-bold text-emerald-800">
                        {office.market_rates?.twelve_month ? formatCurrency(office.market_rates.twelve_month) : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {office.lease_info?.signed_rate && (
                  <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="text-center">
                      <p className="text-sm text-green-600 font-medium">Signed Rate</p>
                      <p className="text-2xl font-bold text-green-800">
                        {formatCurrency(office.lease_info.signed_rate)}/month
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Assignment Tab */}
          <TabsContent value="assignment" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Member Assignment</CardTitle>
              </CardHeader>
              <CardContent>
                {assignedMember ? (
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <Building className="w-5 h-5 mr-2 text-emerald-600" />
                        <span className="font-medium text-emerald-700">Assigned Company</span>
                      </div>
                      {userRole === 'admin' && (
                        <Button 
                          onClick={handleUnassignMember}
                          variant="outline" 
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                        >
                          <Unlink className="w-4 h-4 mr-1" />
                          Unassign
                        </Button>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <p className="text-lg font-semibold text-emerald-800">
                        {assignedMember.company_name}
                      </p>
                      <div className="text-sm text-emerald-600">
                        <div className="flex items-center mb-1">
                          <span className="font-medium">Contact: </span>
                          <span className="ml-1">{assignedMember.contact_name}</span>
                        </div>
                        {assignedMember.contact_email && (
                          <div className="flex items-center mb-1">
                            <Mail className="w-3 h-3 mr-1" />
                            <a href={`mailto:${assignedMember.contact_email}`} className="text-emerald-600 hover:text-emerald-700">
                              {assignedMember.contact_email}
                            </a>
                          </div>
                        )}
                        {assignedMember.contact_phone && (
                          <div className="flex items-center mb-1">
                            <Phone className="w-3 h-3 mr-1" />
                            <a href={`tel:${assignedMember.contact_phone}`} className="text-emerald-600 hover:text-emerald-700">
                              {assignedMember.contact_phone}
                            </a>
                          </div>
                        )}
                        <div className="flex items-center">
                          <Users className="w-3 h-3 mr-1" />
                          <span>{assignedMember.employees?.length || 0} employees</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center">
                    <Building className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-500">No company assigned to this office</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Use the Member Directory to assign a company to this office
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default OfficeProfile;